import { QueryClient } from '@tanstack/react-query';
import { StackBreakpointProvider } from '@teamsparta/stack-core';
import type { GetServerSidePropsContext } from 'next';
import Head from 'next/head';

import type { GetCommunityPostCommentsResponseDto } from '@/api/community/comment/comment.dto';
import type { GetCommunityPostDetailResponseDto } from '@/api/community/post';
import { createServerSideInstanceWithAuth, instanceV2 } from '@/api/instance';
import { Meta } from '@/components/common/Meta';
import {
  communityPostCommentQueryKey,
  communityPostCommentsQuery,
  DEFAULT_SORT_BY,
} from '@/hooks/react-query/community/comment';
import {
  communityPostDetailQuery,
  communityPostQueryKey,
} from '@/hooks/react-query/community/post';
import { env } from '@/lib/utils/env';
import { htmlToText, truncateText } from '@/lib/utils/html';
import { handleRedirect } from '@/lib/utils/redirect';
import { PostDetailTemplate } from '@/pages/community/post/[id]';
import { breakpoints } from '@/styles/themes';

interface Props {
  postDetail: GetCommunityPostDetailResponseDto;
  comments: GetCommunityPostCommentsResponseDto | null;
}

export default function PostDetailPage({ postDetail, comments }: Props) {
  const title = `${truncateText({
    text: postDetail.title,
    maxLength: 30,
    withEllipsis: true,
  })} - 항해99 | 커뮤니티`;
  const description = truncateText({
    text: htmlToText(postDetail.content),
    maxLength: 90,
    withEllipsis: true,
  });

  return (
    <>
      <Head>
        <Meta
          title={title}
          description={description}
          author={postDetail.author.nickname}
          url={`${env.NEXT_PUBLIC_PAGE_URL}/community/post/${postDetail._id}`}
          keywords={`${postDetail.category}, 코딩 부트캠프, 주니어 개발자, 웹개발, 코딩공부, 프로그래밍, 파이썬, 코딩학원, 코딩 스터디, 부트캠프, 개발자, 코딩, 개발자 취업, AWS, 웹서버, 서버, 클라이언트, 자바스크립트, 리액트, React, Spring, 스프링, 앱개발, React Native, 프론트엔드, 백엔드, 풀스택`}
          thumbnail='/images/community/og.webp'
        />
        <meta property='og:title' content={title} />
        <meta property='og:description' content={description} />
        <meta property='og:image' content='/images/community/og.webp' />
        <meta
          property='og:url'
          content={`${env.NEXT_PUBLIC_PAGE_URL}/community/post/${postDetail._id}`}
        />
        <meta property='og:type' content='website' />
        <meta property='og:site_name' content='항해99' />
        <meta property='og:locale' content='ko_KR' />
        <meta property='og:image:width' content='1200' />
        <meta property='og:image:height' content='630' />
        <meta property='og:image:type' content='image/webp' />
        <meta property='og:image:alt' content={title} />

        <style>{`
        body {
          background-color: rgb(20, 22, 23);
        }
      `}</style>
      </Head>
      <StackBreakpointProvider
        mobile={breakpoints.mobile}
        desktop={breakpoints.largeTablet}
      >
        <PostDetailTemplate postDetail={postDetail} comments={comments} />
      </StackBreakpointProvider>
    </>
  );
}

/**
 * @todo community 추상화 필요
 */
export async function getServerSideProps(context: GetServerSidePropsContext) {
  const postId = context.params?.id as string;
  const queryClient = new QueryClient();

  const instanceWithAuth = createServerSideInstanceWithAuth({ context });

  // post detail
  function getCommunityPostDetailWithAuth({ postId }: { postId: string }) {
    return instanceWithAuth.get<GetCommunityPostDetailResponseDto>(
      `/v3/posts/id/${postId}`,
    );
  }
  function getCommunityPostDetail({ postId }: { postId: string }) {
    return instanceV2.get<GetCommunityPostDetailResponseDto>(
      `/v3/posts/id/${postId}`,
    );
  }
  let postDetail: GetCommunityPostDetailResponseDto | undefined;
  try {
    postDetail = await queryClient.fetchQuery(
      communityPostDetailQuery({
        postId,
        queryFn: () => getCommunityPostDetailWithAuth({ postId }),
      }),
    );
  } catch (error) {
    await queryClient.prefetchQuery(
      communityPostDetailQuery({
        postId,
        queryFn: () => getCommunityPostDetail({ postId }),
      }),
    );
    postDetail = queryClient.getQueryData<GetCommunityPostDetailResponseDto>(
      communityPostQueryKey.detail(postId),
    );
  }

  if (!postDetail) {
    return handleRedirect('/404');
  }

  // comments
  function getCommunityPostCommentsWithAuth({ postId }: { postId: string }) {
    return instanceWithAuth.get<GetCommunityPostCommentsResponseDto>(
      `/v3/comments/${postId}`,
      { params: { sortBy: DEFAULT_SORT_BY, limit: 10 } },
    );
  }
  function getCommunityPostComments({ postId }: { postId: string }) {
    return instanceV2.get<GetCommunityPostCommentsResponseDto>(
      `/v3/comments/${postId}`,
      { params: { sortBy: DEFAULT_SORT_BY, limit: 10 } },
    );
  }
  let comments: GetCommunityPostCommentsResponseDto | undefined = undefined;
  try {
    comments = await queryClient.fetchQuery(
      communityPostCommentsQuery({
        postId,
        queryFn: () => getCommunityPostCommentsWithAuth({ postId }),
      }),
    );
  } catch (error) {
    await queryClient.prefetchQuery(
      communityPostCommentsQuery({
        postId,
        queryFn: () => getCommunityPostComments({ postId }),
      }),
    );
    comments = queryClient.getQueryData<GetCommunityPostCommentsResponseDto>(
      communityPostCommentQueryKey.comments({ postId }),
    );
  }

  return {
    props: {
      postDetail: postDetail ?? null,
      comments: comments ?? null,
    },
  };
}
